import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Printer, Copy } from "lucide-react";
import { toast } from "sonner";
import { PaymentMethodIcon, methodLabel } from "@/components/PaymentMethodIcon";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmt = (n, ccy) => new Intl.NumberFormat(undefined, { style: "currency", currency: ccy || "SGD" }).format(n || 0);

export default function PublicInvoice() {
  const { slug } = useParams();
  const [inv, setInv] = useState(null);
  const [dynamicQr, setDynamicQr] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/inv/${slug}`)
      .then((r) => setInv(r.data))
      .catch(() => setError("Invoice not found or link expired."));
    // Fetch a dynamic PayNow QR (with amount + reference). Falls back silently.
    axios.get(`${BACKEND}/api/public/inv/${slug}/paynow-qr`)
      .then((r) => setDynamicQr(r.data))
      .catch(() => setDynamicQr(null));
  }, [slug]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">{error}</div>;
  if (!inv) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading invoice…</div>;

  const profile = inv.billing_profile_snapshot || {};
  const pi = inv.payment_instructions || {};
  const accent = profile.accent_color || "#0f172a";
  const logoUri = profile.logo_data_uri || null;

  return (
    <div className="min-h-screen bg-white text-black" data-testid="public-invoice">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { margin: 15mm 12mm; size: A4; }
          body { background: white !important; }
        }
        .invoice-page { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #1a1a1a; }
        .invoice-page h1 { letter-spacing: -0.02em; }
      `}</style>

      {/* Branded accent ribbon */}
      <div className="h-1.5 w-full" style={{ background: accent }} data-testid="invoice-accent-ribbon" />

      {/* Print action bar */}
      <div className="no-print sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between shadow-sm">
        <div className="text-sm">
          <span className="font-bold">{inv.invoice_number}</span> · {profile.company_name || "Invoice"}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-sm" onClick={() => window.open(`${BACKEND}/api/public/inv/${slug}/pdf`, "_blank")} data-testid="public-invoice-download-pdf">
            <Printer className="h-4 w-4 mr-2" />Download PDF
          </Button>
          <Button variant="outline" className="rounded-sm" onClick={() => { navigator.clipboard.writeText(window.location.href).then(() => toast.success("URL copied")).catch(() => toast.error("Copy failed")); }} data-testid="public-invoice-copy-url">
            <Copy className="h-4 w-4 mr-2" />Copy link
          </Button>
          <Button className="rounded-sm text-white" style={{ backgroundColor: accent }} onClick={() => window.print()} data-testid="public-invoice-print">
            <Printer className="h-4 w-4 mr-2" />Print
          </Button>
        </div>
      </div>

      <div className="invoice-page max-w-4xl mx-auto p-8 md:p-12">
        {/* Header */}
        <div className="flex justify-between items-start mb-10 pb-6 border-b-2" style={{ borderColor: accent }}>
          <div className="flex items-start gap-4">
            {logoUri && (
              <img
                src={logoUri}
                alt="Company logo"
                className="h-16 w-16 object-contain rounded-sm bg-white border border-slate-200 p-1.5 shrink-0"
                data-testid="invoice-logo"
              />
            )}
            <div>
              <h1 className="text-3xl font-black tracking-tight" data-testid="invoice-company-name">{profile.company_name || profile.division_name || "Company Name"}</h1>
              {profile.company_name && profile.division_name && profile.company_name !== profile.division_name && (
                <div className="text-sm text-slate-600 font-semibold mt-0.5" data-testid="invoice-division-name">{profile.division_name}</div>
              )}
              {profile.uen && <div className="text-xs text-slate-500 mt-1">UEN: {profile.uen}</div>}
              <div className="text-sm mt-3 whitespace-pre-line leading-snug text-slate-700">
                {profile.address}{profile.postal_code ? `, Singapore ${profile.postal_code}` : ""}<br />
                {profile.phone && <>Tel: {profile.phone}<br /></>}
                {profile.email && <>Email: {profile.email}<br /></>}
                {profile.website && <>{profile.website}</>}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-4xl font-black tracking-tight" style={{ color: accent }}>INVOICE</div>
            <div className="text-sm mt-1"># <span className="font-mono font-bold">{inv.invoice_number}</span></div>
            <div className="text-sm mt-3 text-slate-600">
              <div><span className="text-slate-400 uppercase text-[10px] tracking-widest">Issue date</span><br />{inv.issue_date}</div>
              {inv.due_date && <div className="mt-2"><span className="text-slate-400 uppercase text-[10px] tracking-widest">Payment due</span><br />{inv.due_date}</div>}
            </div>
          </div>
        </div>

        {/* Bill To */}
        <div className="grid grid-cols-2 gap-6 mb-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1">Bill To</div>
            <div className="font-bold text-lg">{inv.bill_to?.name}</div>
            <div className="text-sm text-slate-700 whitespace-pre-line mt-1 leading-snug">
              {inv.bill_to?.address}{inv.bill_to?.postal_code ? `, Singapore ${inv.bill_to.postal_code}` : ""}
              {inv.bill_to?.phone && <><br />{inv.bill_to.phone}</>}
              {inv.bill_to?.email && <><br />{inv.bill_to.email}</>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1">Amount due ({inv.currency})</div>
            <div className={`text-4xl font-black tracking-tight ${inv.balance_due > 0 ? "text-slate-900" : "text-emerald-600"}`}>{fmt(inv.balance_due, inv.currency)}</div>
            {inv.status === "paid" && <div className="text-xs uppercase tracking-widest text-emerald-600 font-bold mt-1">Paid in full</div>}
          </div>
        </div>

        {/* Line Items */}
        <table className="w-full mb-8" data-testid="public-invoice-items">
          <thead>
            <tr className="border-b border-slate-300">
              <th className="text-left text-[10px] uppercase tracking-widest text-slate-500 font-bold py-2">Description</th>
              <th className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-bold py-2 w-16">Qty</th>
              <th className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-bold py-2 w-24">Price</th>
              <th className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-bold py-2 w-28">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(inv.line_items || []).map((li, i) => (
              <tr key={i} className="border-b border-slate-100 align-top">
                <td className="py-3 text-sm whitespace-pre-wrap">{li.description}{li.include_gst && <span className="text-[10px] uppercase tracking-widest text-slate-400 ml-2">+GST</span>}</td>
                <td className="py-3 text-sm text-right">{li.quantity}</td>
                <td className="py-3 text-sm text-right">{fmt(li.price, inv.currency)}</td>
                <td className="py-3 text-sm text-right">{fmt(li.amount, inv.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="flex justify-end mb-6">
          <div className="w-72 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>{fmt(inv.subtotal, inv.currency)}</span></div>
            {inv.service_fee_amount > 0 && <div className="flex justify-between"><span className="text-slate-500">Service fee ({(inv.service_fee_pct || 0)}%)</span><span>{fmt(inv.service_fee_amount, inv.currency)}</span></div>}
            {inv.gst_amount > 0 && <div className="flex justify-between"><span className="text-slate-500">GST ({((inv.gst_rate || 0) * 100).toFixed(1)}%)</span><span>{fmt(inv.gst_amount, inv.currency)}</span></div>}
            <div className="h-px bg-slate-300 my-2" />
            <div className="flex justify-between font-black text-lg"><span>Total</span><span>{fmt(inv.total, inv.currency)}</span></div>
            {inv.paid_total > 0 && <div className="flex justify-between text-slate-500"><span>Paid</span><span>-{fmt(inv.paid_total, inv.currency)}</span></div>}
            <div className={`flex justify-between font-bold text-lg pt-2 border-t border-slate-300 ${inv.balance_due > 0 ? "" : "text-emerald-600"}`}><span>Balance due</span><span>{fmt(inv.balance_due, inv.currency)}</span></div>
          </div>
        </div>

        {/* Payments received — individual payments with running balance */}
        {(inv.payments || []).length > 0 && (() => {
          const sortedPayments = [...(inv.payments || [])].sort((a, b) => {
            const ka = `${a.date || ""}|${a.recorded_at || ""}`;
            const kb = `${b.date || ""}|${b.recorded_at || ""}`;
            return ka < kb ? -1 : ka > kb ? 1 : 0;
          });
          let running = inv.total || 0;
          return (
            <div className="mb-8" data-testid="public-invoice-payments">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Payments received</div>
              <div className="border border-slate-200 rounded-sm">
                <div className="grid grid-cols-[110px_1fr_140px_120px_120px] gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                  <div>Date</div><div>Method / Reference</div><div className="text-right">Amount paid</div><div className="text-right">Balance after</div><div className="text-right">Receipt</div>
                </div>
                {sortedPayments.map((p, i) => {
                  running = Math.round((running - (p.amount || 0)) * 100) / 100;
                  return (
                    <div key={p.id || i} className="grid grid-cols-[110px_1fr_140px_120px_120px] gap-2 px-3 py-2 border-b border-slate-100 last:border-b-0 text-xs items-center">
                      <div className="text-slate-700">{p.date || "—"}</div>
                      <div className="flex items-center gap-2 min-w-0">
                        <PaymentMethodIcon method={p.method} className="h-3.5 w-3.5 shrink-0" />
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-800">{methodLabel(p.method)}</div>
                          {p.reference && <div className="text-[10px] text-slate-500 truncate" title={p.reference}>Ref: {p.reference}</div>}
                        </div>
                      </div>
                      <div className="text-right font-semibold text-emerald-600">-{fmt(p.amount, inv.currency)}</div>
                      <div className={`text-right font-semibold ${running <= 0.005 ? "text-emerald-600" : "text-slate-900"}`}>{fmt(running, inv.currency)}</div>
                      <div className="text-right">
                        {p.public_slug ? (
                          <a
                            href={`/receipt/${p.public_slug}`}
                            target="_blank" rel="noreferrer"
                            className="text-xs font-semibold underline"
                            style={{ color: accent }}
                            data-testid={`public-invoice-payment-receipt-${i}`}
                          >Open</a>
                        ) : <span className="text-slate-300">—</span>}
                      </div>
                    </div>
                  );
                })}
                <div className="grid grid-cols-[110px_1fr_140px_120px_120px] gap-2 px-3 py-2 border-t-2 border-slate-300 bg-slate-50 text-xs font-bold items-center">
                  <div></div>
                  <div>Balance outstanding</div>
                  <div></div>
                  <div></div>
                  <div className={`text-right ${inv.balance_due > 0.005 ? "text-slate-900" : "text-emerald-600"}`} data-testid="public-invoice-balance-outstanding">{fmt(inv.balance_due, inv.currency)}</div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Payment Instructions */}
        <div className="border border-slate-200 rounded p-6 mb-8 bg-slate-50" data-testid="public-invoice-payment">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-3">Payment methods</div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="text-sm font-bold mb-1">Bank Transfer</div>
              {pi.bank_name && <div className="text-sm">{pi.bank_name}</div>}
              {pi.bank_account_no && <div className="text-sm font-mono">A/C: {pi.bank_account_no}</div>}
              {pi.bank_account_name && <div className="text-xs text-slate-500">Payable to: {pi.bank_account_name}</div>}
              {pi.cheque_payable_to && <div className="text-xs text-slate-500 mt-2">Cheque payable to: {pi.cheque_payable_to}</div>}
            </div>
            <div>
              <div className="text-sm font-bold mb-1">PayNow</div>
              {pi.paynow_uen && <div className="text-sm font-mono mb-2">UEN: {pi.paynow_uen}</div>}
              {dynamicQr?.data_uri ? (
                <>
                  <img src={dynamicQr.data_uri} alt="PayNow QR (amount pre-filled)" className="h-40 w-40 border border-slate-300 rounded bg-white p-2" data-testid="public-invoice-qr-dynamic" />
                  <div className="text-[10px] uppercase tracking-widest text-emerald-600 mt-1 font-bold">Scan to pay {inv.currency} {inv.balance_due.toFixed(2)} — amount &amp; ref included</div>
                </>
              ) : (
                inv.paynow_qr_image && <img src={inv.paynow_qr_image} alt="PayNow QR" className="h-32 w-32 border border-slate-300 rounded" data-testid="public-invoice-qr" />
              )}
            </div>
          </div>
        </div>

        {/* Notes */}
        {inv.notes && (
          <div className="mb-8">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Notes</div>
            <div className="text-sm text-slate-700 whitespace-pre-line">{inv.notes}</div>
          </div>
        )}

        {/* Terms */}
        {(inv.terms || []).length > 0 && (
          <div className="mb-8 pt-6 border-t border-slate-200" data-testid="public-invoice-terms">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-3">Terms &amp; Conditions</div>
            <ol className="text-xs text-slate-700 space-y-1.5 list-decimal ml-5">
              {inv.terms.filter(Boolean).map((t, i) => <li key={i}>{t}</li>)}
            </ol>
          </div>
        )}

        {/* Footer */}
        {profile.default_tagline && (
          <div className="text-center text-xs text-slate-400 italic mt-10 pt-6 border-t border-slate-100">
            ~ {profile.default_tagline} ~
          </div>
        )}
      </div>
    </div>
  );
}
